import { EventEmitter, Injectable } from "@angular/core";
import { EDGE_TYPE, getEdgeType, isOrganisationNode, Linqset, LinqsetEdge, LinqsetNode, NODE_SUPERTYPE, ORGANISATION_MODEL_KEY } from "../common/linqset-model";
import { LabelService } from "./label.service";
import { NodeService } from "./node.service";
import { LABEL_TYPES } from "./model-item-enums";
import { applyChangeToModel, ChangeEvent } from "../model-components/change-event";
import { processLinqset } from "../common/model-processing";
import { Organisation } from "../common/organisation";
import { CHANGE_TYPE } from "../common/linqset-change-model";
import { AdjacencyTypes, GraphComponent, IEdge, IGraph, IModelItem, INode } from "@yfiles/yfiles";

@Injectable({
    providedIn: 'root'
})
export class GraphProcessingService {
    processing:boolean
    // a request to process that came in while processing, run once that finishes; only the latest is kept
    private pendingProcess:() => Promise<void>

    processCompletedEvent = new EventEmitter<boolean>()
    changeEvent = new EventEmitter<ChangeEvent>()

    constructor(private labelService:LabelService, private nodeService:NodeService) {
    }

    createLinqsetNodeChangeEvent(node:LinqsetNode, type:CHANGE_TYPE, preventProcessGraph?:boolean, change?:any) {
        this.changeEvent.emit({
            nodes:[node],
            type,
            change,
            doNotProcessGraph: preventProcessGraph
        } as ChangeEvent)
    }

    createLinqsetNodesChangeEvent(nodes:LinqsetNode[], type:CHANGE_TYPE, preventProcessGraph?:boolean, change?:any) {
        this.changeEvent.emit({
            nodes,
            type,
            change,
            doNotProcessGraph: preventProcessGraph
        } as ChangeEvent)
    }

    createLinqsetEdgesChangeEvent(edges:LinqsetEdge[], type:CHANGE_TYPE, preventProcessGraph?:boolean, change?:any) {
        this.changeEvent.emit({
            edges,
            type,
            change,
            doNotProcessGraph: preventProcessGraph
        } as ChangeEvent)
    }

    createDeletedLinqsetNodesEvent(nodes:LinqsetNode[], preventProcessGraph?:boolean) {
        this.createLinqsetNodesChangeEvent(nodes, CHANGE_TYPE.DELETED, preventProcessGraph)
    }

    createDeletedItemsEvent(items:IModelItem[], preventProcessGraph?:boolean) {
        this.createItemsChangeEvent(items, CHANGE_TYPE.DELETED, preventProcessGraph)
    }

    createAddedItemsEvent(items:IModelItem[], preventProcessGraph?:boolean) {
        this.createItemsChangeEvent(items, CHANGE_TYPE.ADDED, preventProcessGraph)
    }

    createChangesEvent(items:IModelItem[], change:any, preventProcessGraph?:boolean) {
        this.createItemsChangeEvent(items, CHANGE_TYPE.CHANGED, preventProcessGraph || false, change)
    }

    createEdgeChangesEvent(edges:IEdge[], preventProcessGraph?:boolean) {
        this.createItemsChangeEvent(edges, CHANGE_TYPE.CHANGED, preventProcessGraph || false)
    }

    createItemsChangeEvent(items:IModelItem[], type:CHANGE_TYPE, preventProcessGraph?:boolean, change?:any) {
        const event = {
            type,
            items,
            change: change,
            doNotProcessGraph: preventProcessGraph
        } as ChangeEvent

        event.nodes = []
        event.edges = []

        items.forEach(i => {
            if (i.tag?.node) {
                event.nodes.push(i.tag.node)
            }else if (i.tag?.edge) {
                // the ends may have moved, so the edge takes its keys and type from them again
                if (type !== CHANGE_TYPE.DELETED && i instanceof IEdge && i.sourceNode.tag?.node && i.targetNode.tag?.node) {
                    i.tag.edge.sourceKey = i.sourceNode.tag.node._key
                    i.tag.edge.sourceVisualisationKey = i.sourceNode.tag.visualisationKey
                    i.tag.edge.targetKey = i.targetNode.tag.node._key
                    i.tag.edge.targetVisualisationKey = i.targetNode.tag.visualisationKey
                    const edgeType = getEdgeType(i.sourceNode.tag.node, i.targetNode.tag.node)
                    // "used in" is a join the user chose not to pass items through; the ends alone would make it a join again
                    if (!(i.tag.edge.type === EDGE_TYPE.USED_IN && edgeType === EDGE_TYPE.JOINS)) i.tag.edge.type = edgeType
                }
                if (type === CHANGE_TYPE.ADDED && i instanceof IEdge && isOrganisationNode(i.sourceNode?.tag?.node?.superType))  {
                    event.nodes.push(i.sourceNode.tag.node)
                }
                event.edges.push(i.tag.edge)
            }
        })

        this.changeEvent.emit(event)
    }

    async handleEditModelGraphEvent(organisation:Organisation, graphComponent:GraphComponent, linqset:Linqset, organisationLinqset:Linqset, event:ChangeEvent) {
        await applyChangeToModel(event, linqset, organisationLinqset, this.nodeService)

        if (event.items && event.type === CHANGE_TYPE.CHANGED && linqset.isProjectModel()) {
            event.items.filter(i => (i instanceof INode)).forEach(i => {
                this.nodeService.setNodeStyle(graphComponent.graph, i, linqset)
            })
        }

        // new nodes do not impact insights be themselves, need to be connected
        const doNotProcess = event.doNotProcessGraph || (event.change && (event.change.description || event.change.name || event.change.tagKeys || event.change.notes || event.change.url))

        if (doNotProcess && event.items && event.change) {
            event.items.filter((i):i is INode => (i instanceof INode) && !!i.tag?.node).forEach(i => {
                if (event.change?.name) {
                    this.labelService.replaceLabelForLinqsetNode(graphComponent.graph, i.tag.node, event.change.name, LABEL_TYPES.NODE_NAME)
                }

                if (event.change?.tagKeys) {
                    this.labelService.updateLabelsForNode(graphComponent.graph, i, linqset, undefined)
                }

            })
            this.processCompletedEvent.emit(false)
        }else {
            linqset.refresh = "model"
            // check if made a property change to an organisation node, if so then need to refresh the organisation insights
            if (event.change?.properties && event.nodes?.some(n => n.modelKey === ORGANISATION_MODEL_KEY)) linqset.refresh = "organisation"

            await this.processGraph(organisation, graphComponent.graph, linqset, organisationLinqset, false)
        }
    }

    public getStartingNodes(graph:IGraph) {
        return graph.nodes.filter(n => {
            if (!n.tag || !n.tag.node || (n.tag.node.superType !== NODE_SUPERTYPE.INFORMATION && n.tag.node.superType !== NODE_SUPERTYPE.ACTION)) return false
            const superType = n.tag.node.superType === NODE_SUPERTYPE.ACTION?NODE_SUPERTYPE.INFORMATION:NODE_SUPERTYPE.ACTION
            return graph.edgesAt(n, AdjacencyTypes.INCOMING).filter(e => e.sourceNode.tag && e.sourceNode.tag.node && e.sourceNode.tag.node.superType === superType).size === 0
        })
    }

    public async processGraph(organisation:Organisation, graph:IGraph, linqset:Linqset, organisationLinqset:Linqset, ignoreFilter:boolean) {
        if (this.processing) {
            // a change made while processing (a second edit, or a filter) would otherwise not be reflected
            this.pendingProcess = () => this.processGraph(organisation, graph, linqset, organisationLinqset, ignoreFilter)
            return
        }
        this.processing = true

        try {
            await processLinqset(linqset, organisationLinqset, ignoreFilter, organisation)
            this.labelService.updateLabelsForGraph(graph, true, linqset, undefined)
            this.processCompletedEvent.emit(true)

        }catch(err) {
            console.error("Exception while processing model", err)
        }finally {
            this.processing = false
        }

        const next = this.pendingProcess
        this.pendingProcess = undefined
        if (next) await next()
    }
}
